import {
  buildTimesheetDataset,
  exportDataset,
  liveProjectFilters,
  liveTimeEntryFilters,
  queryKeys,
} from "@time-tracker/shared";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { chronosRoute } from "../chronos";
import { GlassSurface, PageContainer, PageHeader } from "@fleet/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select } from "@/components/ui/select";
import { TimesheetEmptyIllustration } from "@/components/ui/illustrations";
import { Download, FileSpreadsheet, FileText, Receipt } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { WorkspaceSwitcher } from "@/features/chronos-sync/components/workspace-switcher";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import {
  and,
  eq,
  project,
  type Project,
  project_version,
  type ProjectVersionRecord,
  time_entry,
  type TimeEntry,
} from "@time-tracker/powersync";
import {
  aggregateTimesheet,
  GRANULARITIES,
  type Granularity,
  isoToLocalDay,
} from "@/features/chronos-sync/timesheet";
import {
  getGlobalRate,
  getProjectRates,
  resolveRate,
  setGlobalRate,
  setProjectRate,
} from "@/features/chronos-sync/rates";
import {
  getRoundingConfig,
  ROUNDING_INCREMENTS_MIN,
  ROUNDING_MODES,
  type RoundingMode,
  setRoundingConfig,
} from "@/features/chronos-sync/rounding";

const MS_IN_DAY = 86_400_000;
const DEFAULT_RANGE_DAYS = 30;
const HOURS_DECIMALS = 2;
const AMOUNT_DECIMALS = 2;
const RATE_BUMP = 0;

/** Today as YYYY-MM-DD (local), for the date inputs. */
function today(): string {
  return isoToLocalDay(new Date().toISOString());
}

/** N days ago as YYYY-MM-DD (local). */
function daysAgo(n: number): string {
  return isoToLocalDay(new Date(Date.now() - n * MS_IN_DAY).toISOString());
}

function ChronosTimesheetPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();
  const delivery = useKernelService(EXPORT_DELIVERY);

  // Deep-link support: analytics bars / project rows navigate here with a
  // pre-filled date range (and granularity). Falls back to the default window.
  // The filter is fully shareable: editing any control writes it back to the URL
  // (replace), and an effect mirrors external URL changes (back/forward + pasted
  // links) into the signals → idempotent on reload.
  const search = useSearch({ from: chronosTimesheetRoute.id });
  const navigate = useNavigate();

  const [fromDate, setFromDate] = createSignal(search().from ?? daysAgo(DEFAULT_RANGE_DAYS));
  const [toDate, setToDate] = createSignal(search().to ?? today());
  const [granularity, setGranularity] = createSignal<Granularity>(search().granularity ?? "day");

  /** Mirror the current filter into the URL (omit defaults for clean links). */
  const syncUrl = () => {
    void navigate({
      to: chronosTimesheetRoute.to,
      // Preserve other search params (e.g. `?ws=`) and only touch range.
      search: (prev) => ({
        ...prev,
        from: fromDate(),
        to: toDate(),
        granularity: granularity() === "day" ? undefined : granularity(),
      }),
      replace: true,
    });
  };

  // Comparisons in `untrack`: effect only reacts to EXTERNAL changes of the
  // URL. If tracking the signals it writes, a state-first change (from/to/
  // granularity + async syncUrl, which reads those signals) would re-trigger with
  // the old search and revert the range deterministically (bug #15b / CC-16).
  createEffect(() => {
    const urlFrom = search().from;
    const urlTo = search().to;
    const urlGranularity = search().granularity ?? "day";
    if (urlFrom !== undefined && untrack(() => urlFrom !== fromDate())) {
      setFromDate(urlFrom);
    }
    if (urlTo !== undefined && untrack(() => urlTo !== toDate())) {
      setToDate(urlTo);
    }
    if (untrack(() => urlGranularity !== granularity())) {
      setGranularity(urlGranularity);
    }
  });

  // Rate config (localStorage-backed). A signal bump forces recompute after edits.
  const [rateVersion, setRateVersion] = createSignal(RATE_BUMP);
  const [globalRate, setGlobalRateSignal] = createSignal(getGlobalRate());

  // Billing rounding (device-local, rate pattern): per-entry, Toggl-style.
  const [rounding, setRounding] = createSignal(getRoundingConfig());
  const updateRounding = (patch: Partial<ReturnType<typeof getRoundingConfig>>) => {
    const next = { ...rounding(), ...patch };
    setRounding(next);
    setRoundingConfig(next);
  };

  // Projects of the active workspace (for labels + per-project rate editing).
  const projectsQuery = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.projectsList(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  // All non-deleted time entries for the user; filtered to range in the memo.
  const entriesQuery = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.timeline.timeEntriesHistory(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(time_entry)
        .where(and(eq(time_entry.user_id, userId()), ...liveTimeEntryFilters()));
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Local project versions (rate-sealing): the window bucket (last 10/project).
  // Used to look up the SEALED rate of each entry by project_version_id.
  const versionsQuery = createPowerSyncQuery<ProjectVersionRecord>({
    queryKey: () => queryKeys.chronos.projectVersionsAll(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? dbInstance.select().from(project_version) : null;
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  // oxlint-disable-next-line lingui/no-unlocalized-strings -- date/ISO format, not UI text
  const rangeStart = createMemo(() => `${fromDate()}T00:00:00.000`);
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- date/ISO format, not UI text
  const rangeEnd = createMemo(() => `${toDate()}T23:59:59.999`);

  const rows = createMemo(() => {
    // Track rateVersion so edits to per-project rates re-run the aggregation.
    rateVersion();
    const projectRates = getProjectRates();
    const gRate = globalRate();
    // Sealed-rate lookup: project_version_id → {rate, currency}.
    const sealedById = new Map<string, { rate: number | null; currency: string | null }>();
    for (const v of versionsQuery.data ?? []) {
      sealedById.set(v.id, { rate: v.hourly_rate ?? null, currency: v.currency ?? null });
    }
    // Synced project rate: project_id → {rate, currency}.
    const syncedByProject = new Map<string, { rate: number | null; currency: string | null }>();
    for (const p of projectsQuery.data ?? []) {
      syncedByProject.set(p.id, { rate: p.hourly_rate ?? null, currency: p.currency ?? null });
    }
    return aggregateTimesheet({
      entries: entriesQuery.data ?? [],
      projects: projectsQuery.data ?? [],
      rangeStart: rangeStart(),
      rangeEnd: rangeEnd(),
      granularity: granularity(),
      resolveRate: (projectId) => resolveRate(projectId, projectRates, gRate),
      // Sealed → synced project → localStorage. A block keeps the rate it was
      // billed at even after the project's rate changes (no retroactive recalc).
      rounding: rounding(),
      resolveEntryRate: (entry) => {
        const sealed = entry.project_version_id
          ? sealedById.get(entry.project_version_id)
          : undefined;
        if (sealed && sealed.rate !== null) {
          return { rate: sealed.rate, currency: sealed.currency };
        }
        const synced = entry.project_id ? syncedByProject.get(entry.project_id) : undefined;
        if (synced && synced.rate !== null) {
          return { rate: synced.rate, currency: synced.currency };
        }
        return { rate: resolveRate(entry.project_id, projectRates, gRate), currency: null };
      },
      noProjectLabel: t(msg`No Project`),
    });
  });

  const totalHours = createMemo(() => rows().reduce((sum, r) => sum + r.hours, 0));
  const totalAmount = createMemo(() => rows().reduce((sum, r) => sum + r.amount, 0));

  const buildDataset = () =>
    buildTimesheetDataset(rows(), {
      title: "Timesheet",
      subtitle: `${fromDate()} → ${toDate()}`,
      extra: { Granularity: granularity() },
    });

  const handleExportCsv = () => {
    const target = delivery();
    if (!target) {
      return;
    }
    void exportDataset(buildDataset(), "csv", target);
  };

  const handleExportPdf = () => {
    const target = delivery();
    if (!target) {
      return;
    }
    void exportDataset(buildDataset(), "pdf", target);
  };

  const handleGlobalRateInput = (value: string) => {
    const n = Number(value);
    const next = Number.isFinite(n) ? n : 0;
    setGlobalRateSignal(next);
    setGlobalRate(next);
    setRateVersion((v) => v + 1);
  };

  const handleProjectRateInput = (projectId: string, value: string) => {
    if (value.trim() === "") {
      setProjectRate(projectId, null);
    } else {
      const n = Number(value);
      setProjectRate(projectId, Number.isFinite(n) ? n : null);
    }
    setRateVersion((v) => v + 1);
  };

  const projectRateValue = (projectId: string) => {
    rateVersion();
    const map = getProjectRates();
    return projectId in map ? String(map[projectId]) : "";
  };

  return (
    <PageContainer
      maxWidth="5xl"
      class="space-y-8 text-white"
      containerClass="bg-[#000000] text-white pb-[calc(6.5rem+var(--safe-area-inset-bottom))]"
    >
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <PageHeader
          title={t(msg`Timesheet`)}
          description={t(msg`Aggregate tracked time and bill it by hourly rate.`)}
          icon={Receipt}
        />
        <div class="flex items-center gap-2">
          <WorkspaceSwitcher />
          <Popover>
            <PopoverTrigger
              type="button"
              class="inline-flex h-9 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-on-primary hover:bg-primary/90 cursor-pointer"
            >
              <Download class="h-4 w-4" />
              {t(msg`Export`)}
            </PopoverTrigger>
            <PopoverContent class="bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-[16px] p-1.5 shadow-2xl w-44 z-50">
              <button
                type="button"
                id="export-csv"
                onClick={handleExportCsv}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-foreground"
              >
                <FileSpreadsheet class="h-3.5 w-3.5" /> {t(msg`Export CSV`)}
              </button>
              <button
                type="button"
                id="export-pdf"
                onClick={handleExportPdf}
                class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-foreground"
              >
                <FileText class="h-3.5 w-3.5" /> {t(msg`Export PDF`)}
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Range + granularity controls */}
      <GlassSurface class="p-5 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div class="space-y-1">
            <label
              for="ts-from"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`From`)}
            </label>
            <input
              id="ts-from"
              type="date"
              value={fromDate()}
              onInput={(e) => {
                setFromDate(e.currentTarget.value);
                syncUrl();
              }}
              class="w-full bg-black/40 border border-white/10 rounded-[16px] px-3 py-2.5 text-xs text-white font-mono [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40"
            />
          </div>
          <div class="space-y-1">
            <label
              for="ts-to"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`To`)}
            </label>
            <input
              id="ts-to"
              type="date"
              value={toDate()}
              onInput={(e) => {
                setToDate(e.currentTarget.value);
                syncUrl();
              }}
              class="w-full bg-black/40 border border-white/10 rounded-[16px] px-3 py-2.5 text-xs text-white font-mono [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40"
            />
          </div>
          <div class="space-y-1">
            <label
              for="ts-granularity"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`Group by`)}
            </label>
            <Select
              id="ts-granularity"
              aria-label={t(msg`Group by`)}
              value={granularity()}
              onChange={(value) => {
                const next = GRANULARITIES.find((g) => g === value);
                if (next) {
                  setGranularity(next);
                  syncUrl();
                }
              }}
              class="px-3 py-2.5 text-xs font-mono"
              options={[
                { value: "day", label: t(msg`Day`) },
                { value: "week", label: t(msg`Week`) },
                { value: "month", label: t(msg`Month`) },
              ]}
            />
          </div>
          <div class="space-y-1">
            <label
              for="ts-global-rate"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`Default rate / h`)}
            </label>
            <input
              id="ts-global-rate"
              type="number"
              min="0"
              step="0.01"
              value={globalRate()}
              onInput={(e) => {
                handleGlobalRateInput(e.currentTarget.value);
              }}
              class="w-full bg-black/40 border border-white/10 rounded-[16px] px-3 py-2.5 text-xs text-white font-mono [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40"
            />
          </div>
          <div class="space-y-1">
            <label
              for="ts-rounding-mode"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`Rounding`)}
            </label>
            <Select
              id="ts-rounding-mode"
              aria-label={t(msg`Rounding`)}
              value={rounding().mode}
              onChange={(value) => {
                const next = ROUNDING_MODES.find((m) => m === value);
                if (next) {
                  updateRounding({ mode: next });
                }
              }}
              class="px-3 py-2.5 text-xs font-mono"
              options={
                [
                  { value: "none", label: t(msg`Exact (no rounding)`) },
                  { value: "up", label: t(msg`Round up`) },
                  { value: "nearest", label: t(msg`Round to nearest`) },
                  { value: "down", label: t(msg`Round down`) },
                ] satisfies { value: RoundingMode; label: string }[]
              }
            />
          </div>
          <div class="space-y-1">
            <label
              for="ts-rounding-increment"
              class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
            >
              {t(msg`Increment`)}
            </label>
            <Select
              id="ts-rounding-increment"
              aria-label={t(msg`Increment`)}
              value={String(rounding().minutes)}
              disabled={rounding().mode === "none"}
              onChange={(value) => {
                const next = ROUNDING_INCREMENTS_MIN.find((m) => String(m) === value);
                if (next) {
                  updateRounding({ minutes: next });
                }
              }}
              class="px-3 py-2.5 text-xs font-mono"
              options={ROUNDING_INCREMENTS_MIN.map((m) => ({
                value: String(m),
                label: t(msg`${m} min`),
              }))}
            />
          </div>
        </div>
      </GlassSurface>

      {/* Per-project rate overrides */}
      <Show when={(projectsQuery.data ?? []).length > 0}>
        <GlassSurface class="p-5 bg-[#121212] border border-white/10 rounded-[24px] shadow-none">
          <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            {t(msg`Per-project rates`)}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <For each={projectsQuery.data}>
              {(p) => {
                const projectLabel = p.name ?? "";
                return (
                  <div class="flex items-center justify-between gap-2 bg-black/30 border border-white/5 rounded-[16px] px-3 py-2">
                    <span class="text-xs font-mono text-foreground truncate">{p.name}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      aria-label={t(msg`Rate for ${projectLabel}`)}
                      placeholder={t(msg`default`)}
                      value={projectRateValue(p.id)}
                      onInput={(e) => {
                        handleProjectRateInput(p.id, e.currentTarget.value);
                      }}
                      class="w-20 bg-black/40 border border-white/10 rounded-[12px] px-2 py-1.5 text-xs text-white font-mono [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40"
                    />
                  </div>
                );
              }}
            </For>
          </div>
        </GlassSurface>
      </Show>

      {/* Timesheet table */}
      <GlassSurface class="p-0 bg-[#121212] border border-white/10 rounded-[24px] shadow-none overflow-hidden">
        <Show
          when={rows().length > 0}
          fallback={
            <div class="text-center py-12 motion-safe:animate-fade-in">
              <TimesheetEmptyIllustration class="h-28 w-28 mx-auto mb-2" />
              <h3 class="text-sm font-bold text-foreground font-mono">
                {t(msg`No time in this range`)}
              </h3>
              <p class="text-xs text-muted-foreground mt-1 font-mono">
                {t(msg`Adjust the dates or log some time first.`)}
              </p>
            </div>
          }
        >
          <table class="w-full table-fixed text-left text-xs font-mono">
            <thead>
              <tr class="border-b border-white/10 text-muted-foreground uppercase tracking-wider">
                <th class="px-2 py-3 font-semibold sm:px-4">{t(msg`Project`)}</th>
                <th class="hidden px-2 py-3 font-semibold sm:table-cell sm:px-4">
                  {t(msg`Period`)}
                </th>
                <th class="px-2 py-3 font-semibold text-right sm:px-4">{t(msg`Hours`)}</th>
                <th class="hidden px-2 py-3 font-semibold text-right sm:table-cell sm:px-4">
                  {t(msg`Rate`)}
                </th>
                <th class="px-2 py-3 font-semibold text-right sm:px-4">{t(msg`Amount`)}</th>
              </tr>
            </thead>
            <tbody>
              <For each={rows()}>
                {(r) => (
                  <tr class="border-b border-white/5 hover:bg-white/[0.02]">
                    <td class="px-2 py-2.5 text-foreground font-bold sm:px-4">
                      <span class="block truncate">{r.project}</span>
                      <span class="block truncate text-[10px] font-normal text-muted-foreground sm:hidden">
                        {r.period}
                      </span>
                    </td>
                    <td class="hidden px-2 py-2.5 text-muted-foreground sm:table-cell sm:px-4">
                      {r.period}
                    </td>
                    <td class="px-2 py-2.5 text-right text-foreground sm:px-4">
                      {r.hours.toFixed(HOURS_DECIMALS)}
                    </td>
                    <td class="hidden px-2 py-2.5 text-right text-muted-foreground sm:table-cell sm:px-4">
                      {r.rate.toFixed(AMOUNT_DECIMALS)}
                      <Show when={r.currency}>
                        <span class="ml-1 text-[10px] text-white/40">{r.currency}</span>
                      </Show>
                    </td>
                    <td
                      class="px-2 py-2.5 text-right text-[#ceffdf] sm:px-4"
                      data-testid="ts-row-amount"
                    >
                      {r.amount.toFixed(AMOUNT_DECIMALS)}
                      <Show when={r.currency}>
                        <span class="ml-1 text-[10px] text-[#ceffdf]/50">{r.currency}</span>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
            <tfoot>
              <tr class="border-t border-white/10 text-foreground font-bold">
                <td class="px-2 py-3 sm:px-4" colspan="1">
                  {t(msg`Total`)}
                </td>
                <td class="hidden px-2 py-3 sm:table-cell sm:px-4" />
                <td id="ts-total-hours" class="px-2 py-3 text-right sm:px-4">
                  {totalHours().toFixed(HOURS_DECIMALS)}
                </td>
                <td class="hidden px-2 py-3 sm:table-cell sm:px-4" />
                <td id="ts-total-amount" class="px-2 py-3 text-right text-[#ceffdf] sm:px-4">
                  {totalAmount().toFixed(AMOUNT_DECIMALS)}
                </td>
              </tr>
            </tfoot>
          </table>
        </Show>
      </GlassSurface>
    </PageContainer>
  );
}

interface TimesheetSearch {
  from?: string;
  to?: string;
  granularity?: Granularity;
  wsdialog?: "new";
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const isYmd = (v: unknown): v is string => typeof v === "string" && YMD_RE.test(v);

export const chronosTimesheetRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/timesheet",
  validateSearch: (raw: Record<string, unknown>): TimesheetSearch => {
    const g = raw.granularity;
    return {
      from: isYmd(raw.from) ? raw.from : undefined,
      to: isYmd(raw.to) ? raw.to : undefined,
      granularity: GRANULARITIES.find((x) => x === g),
      wsdialog: raw.wsdialog === "new" ? "new" : undefined,
    };
  },
  component: ChronosTimesheetPage,
});
